import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { LiliButton } from '@lili/lili-frontend-common';
import Heading from '../../../generic-components/Heading';
import { eddContentMap, eddCountriesList, MoveMoneySteps } from '../consts';
import { VIEWS_MAP } from '../../../router/constants';
import { NavigationHeader } from '../components/NavigationHeader';
import { LiliRadioChipGroup, RadioItemStyle } from '../../../generic-components/LiliRadioChipGroup/LiliRadioChipGroup';
import { LiliMultiSelect } from '../../../DesignSystem/LiliMultiSelect/LiliMultiSelect';
import { postUploadEdd } from '../api/postUploadEdd';
import { useMoveMoneyLogic } from '../hooks/useMoveMoneyLogic';

export interface IEddAnswers {
  sendingCountries: string[];
  expectedMonthlyTransfersCount: string;
  expectedMonthlyTransfersVolume: string;
}

export const InternationalWireEdd = () => {
  const { moveType } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { moveMoneyData, setMoveMoneyData, resetMoveMoneyData } = useMoveMoneyLogic();

  const { mutateAsync, isLoading } = postUploadEdd();

  const [eddAnswers, setEddAnswers] = React.useState<IEddAnswers>({
    sendingCountries: moveMoneyData.eddAnswers?.sendingCountries || [],
    expectedMonthlyTransfersCount: moveMoneyData.eddAnswers?.expectedMonthlyTransfersCount || '',
    expectedMonthlyTransfersVolume: moveMoneyData.eddAnswers?.expectedMonthlyTransfersVolume || '',
  });

  const handleCloseFlow = () => {
    resetMoveMoneyData();
    navigate(`/${VIEWS_MAP.DASHBOARD}`);
  };

  const handleBackButton = () => {
    setMoveMoneyData({ ...moveMoneyData, activeStepId: MoveMoneySteps.PURPOSE_OF_TRANSFER });
  };

  const answers2: string[] = t(eddContentMap[moveType as keyof typeof eddContentMap].answers2, { returnObjects: true });
  const answers3: string[] = t(eddContentMap[moveType as keyof typeof eddContentMap].answers3, { returnObjects: true });

  const isSubmitDisabled = useMemo(() => {
    if (!eddAnswers.sendingCountries.length) return true;
    if (!eddAnswers.expectedMonthlyTransfersCount) return true;
    return !eddAnswers.expectedMonthlyTransfersVolume;
  }, [eddAnswers]);

  const handleContinue = async () => {
    await mutateAsync({
      version: moveMoneyData.eddVersion,
      results: [
        {
          question: t(eddContentMap[moveType as keyof typeof eddContentMap].question1),
          answer: eddAnswers.sendingCountries.join(', '),
        },
        {
          question: t(eddContentMap[moveType as keyof typeof eddContentMap].question2),
          answer: eddAnswers.expectedMonthlyTransfersCount,
        },
        {
          question: t(eddContentMap[moveType as keyof typeof eddContentMap].question3),
          answer: eddAnswers.expectedMonthlyTransfersVolume,
        },
      ],
    });
    setMoveMoneyData({ ...moveMoneyData, eddAnswers, activeStepId: MoveMoneySteps.REVIEW });
  };

  const getDefaultAnswer2 = () => {
    if (moveMoneyData.eddAnswers?.expectedMonthlyTransfersCount) {
      return answers2.findIndex((answer) => answer === moveMoneyData.eddAnswers?.expectedMonthlyTransfersCount);
    }
    return null;
  };

  const getDefaultAnswer3 = () => {
    if (moveMoneyData.eddAnswers?.expectedMonthlyTransfersCount) {
      return answers3.findIndex((answer) => answer === moveMoneyData.eddAnswers?.expectedMonthlyTransfersVolume);
    }
    return null;
  };

  return (
    <div className='fixed left-0 top-0 w-full h-full bg-bw01 z-50 flex flex-col'>
      <NavigationHeader disableMarginBottom onBack={handleBackButton} onClose={handleCloseFlow} />

      <div className='flex flex-col h-full overflow-auto items-center gap-[4px] w-[460px] pt-[64px] pb-[20px] grow mx-auto no-scrollbar no-scrollbar::-webkit-scrollbar'>
        <Heading
          title={t(eddContentMap[moveType as keyof typeof eddContentMap].title)}
          className='mb-[8px] w-full text-start font-metricRMedium text-bw08 text-[28px]'
        />
        <Heading
          title={t(eddContentMap[moveType as keyof typeof eddContentMap].content)}
          className='mb-[24px] w-full font-metricR text-bw08 text-[16px]'
        />

        <Heading
          title={t(eddContentMap[moveType as keyof typeof eddContentMap].question1)}
          className='mx-auto mb-[8px] font-metricRMedium text-bw08 text-[16px]'
        />

        <Heading
          title={t(eddContentMap[moveType as keyof typeof eddContentMap].hint1)}
          className='mx-auto w-full mb-[22px] font-metricR text-bw08 text-[14px]'
        />

        <div className='mb-[24px] mx-auto w-full'>
          <LiliMultiSelect
            onChange={(selectedValues) => setEddAnswers({ ...eddAnswers, sendingCountries: selectedValues })}
            maxSelectedLabels={3}
            items={eddCountriesList}
            defaultValue={moveMoneyData.eddAnswers?.sendingCountries}
          />
        </div>

        <Heading
          title={t(eddContentMap[moveType as keyof typeof eddContentMap].question2)}
          className='mx-auto w-full mb-[22px] font-metricRMedium text-bw08 text-[16px]'
        />

        <div className='flex w-full mb-[48px]'>
          <LiliRadioChipGroup
            items={[
              {
                value: answers2[0],
                label: answers2[0],
              },
              {
                value: answers2[1],
                label: answers2[1],
              },
              {
                value: answers2[2],
                label: answers2[2],
              },
              {
                value: answers2[3],
                label: answers2[3],
              },
            ]}
            onChange={(value) => setEddAnswers({ ...eddAnswers, expectedMonthlyTransfersCount: value })}
            groupId='second-question-answers'
            isVertical
            radioItemStyle={RadioItemStyle.REGULAR}
            defaultSelectedItemIndex={getDefaultAnswer2()}
          />
        </div>

        <Heading
          title={t(eddContentMap[moveType as keyof typeof eddContentMap].question3)}
          className='mx-auto w-full mb-[22px] font-metricRMedium text-bw08 text-[16px]'
        />

        <div className='flex w-full mb-2'>
          <LiliRadioChipGroup
            items={[
              {
                value: answers3[0],
                label: answers3[0],
              },
              {
                value: answers3[1],
                label: answers3[1],
              },
              {
                value: answers3[2],
                label: answers3[2],
              },
              {
                value: answers3[3],
                label: answers3[3],
              },
            ]}
            onChange={(value) => setEddAnswers({ ...eddAnswers, expectedMonthlyTransfersVolume: value })}
            groupId='second-question-answers'
            isVertical
            radioItemStyle={RadioItemStyle.REGULAR}
            defaultSelectedItemIndex={getDefaultAnswer3()}
          />
        </div>
      </div>

      <div className='w-full border-t border-bw03 flex items-center justify-center py-[24px]'>
        <LiliButton
          onClick={handleContinue}
          disabled={isSubmitDisabled || isLoading}
          isLoading={isLoading}
          id='continue'
          className='w-[375px]'
          title={t('MOVE_MONEY.STEPS.PURPOSE_OF_TRANSFER.CTA')}
        />
      </div>
    </div>
  );
};
